import React, { useEffect, useState } from "react"
import { Tuple } from "components/Shared/Interfaces"
import { FormattedMessage } from "react-intl"
import {
	Collapse,
	Divider,
	Heading,
} from "@walmart-web/livingdesign-components"
import { renderCurrentFilter } from "./utils"
import { concatClassNames } from "helpers/jsxHelpers"
import "./components.scss"
import { ChevronDown, ChevronUp } from "@walmart-web/livingdesign-icons"

interface PanelProps {
	id: string
	selectedValues: Array<Tuple>
	isDisabled?: boolean
	isCollapsable?: boolean
	codeLabels?: boolean
	selectedLabel?: string
	disabledText?: string
}

interface CollapseProps {
	isCollapsable: boolean
	isOpen: boolean
}

interface ContentProps extends CollapseProps {
	isDisabled?: boolean
	disabledText: string
	selectedLabel: string
	currentSelected: string
}

export const CollapseIcon: React.FC<CollapseProps> = ({
	isCollapsable,
	isOpen,
}) => {
	if (!isCollapsable) return <React.Fragment />

	return isOpen ? <ChevronUp /> : <ChevronDown />
}

const Content: React.FC<ContentProps> = ({
	isCollapsable,
	isDisabled,
	isOpen,
	disabledText,
	children,
	selectedLabel,
	currentSelected,
}) => {
	if (isDisabled)
		return (
			<div className="disabled">
				<FormattedMessage id={disabledText} />
			</div>
		)

	return (
		<React.Fragment>
			{isCollapsable ? (
				<Collapse isOpen={isOpen}>{children}</Collapse>
			) : (
				children
			)}
			<span className="filter-current">
				<FormattedMessage id={selectedLabel} />
				{currentSelected}
			</span>
		</React.Fragment>
	)
}

const PanelContent: React.FC<PanelProps> = ({
	id,
	isCollapsable = false,
	isDisabled,
	selectedValues,
	codeLabels,
	children,
	selectedLabel = "phronesis.filters.current",
	disabledText = "phronesis.filters.disabled.panel",
}) => {
	const [currentSelected, setCurrentSelected] = useState<string>("")
	const [isOpen, setIsOpen] = useState<boolean>(false)
	const title = `phronesis.filters.${id}`

	useEffect(() => {
		setCurrentSelected(renderCurrentFilter(selectedValues, codeLabels))
	}, [selectedValues])

	return (
		<div className="panel-content">
			<Heading
				as="h5"
				data-testid={`title-${title}`}
				UNSAFE_className={concatClassNames(["title", isDisabled && "disabled"])}
				onClick={() => setIsOpen(!isOpen)}
			>
				<FormattedMessage id={title} />
				<CollapseIcon isCollapsable={isCollapsable} isOpen={isOpen} />
			</Heading>
			<Content
				isCollapsable={isCollapsable}
				isDisabled={isDisabled}
				isOpen={isOpen}
				disabledText={disabledText}
				selectedLabel={selectedLabel}
				currentSelected={currentSelected}
			>
				{children}
			</Content>
			<Divider UNSAFE_className="divider" />
		</div>
	)
}

export default PanelContent
